import React, { Component } from "react";
import { Row, Col, Button, Input } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { withRouter } from "react-router-dom";
import { withAppContext } from "../store/contextStore";
import HomeBuddha from "../common/HomeBuddha";
import { sendHomeClaimedWebhook } from "../utils/ghlWebhooks";

const NAVY   = "#1A2280";
const GOLD   = "#D4921A";
const GREEN  = "#2D6A4F";
const MUTED  = "#718096";
const BORDER = "#E2E8F0";

// Picks the first candidate that's an actual string/number and coerces it.
// property-data fields are supposed to be primitives, but if one ever comes
// back as an object, a bare String(candidateA || candidateB || "") would
// silently stringify it to "[object Object]" instead of falling through to
// the next candidate — same class of bug as the one fixed in
// extractOwnerName() below, just for the numeric/text fields instead of
// the owner name. Mirrors the same helper added to HomeBuddha.js.
function pickStr(...candidates) {
  for (const v of candidates) {
    if (v && (typeof v === "string" || typeof v === "number")) return String(v);
  }
  return "";
}

// Stage 1: standalone public route for the Home Portal, reachable directly
// from the header nav (not just buried in My Tools). Renders the exact same
// <HomeBuddha /> component the "Home Buddha" tab in ViewProfile.js uses —
// nothing about that tab changes, this is just a second entry point to the
// same component.
//
// Stage 2: anonymous visitors can browse every section/subsection and use
// AI chat freely. Only "Set Up My Home" (creating a home profile) and the
// Home Health Check-Up quiz (gated inside HomeBuddha.js itself) require
// login. Optional /:sectionKey/:subId route params let an ad or post deep
// link straight into a specific subsection (e.g. water source quality).
//
// Marketing hook (below) is pulled from the "Home Portal" brand one-pager
// and only shows for logged-out visitors, same pattern as ValueTrackerPage.js.
class HomePortalPage extends Component {
constructor(props) {
super(props);
this.state = {
showSetupMyHome: false,
// Bumped to pop the (already-mounted, below) <HomeBuddha> component's
// Health Check-Up quiz modal open from the banner in renderHook() — see
// HomeBuddha.js's componentDidUpdate/openHealthCheckSignal prop. A counter
// rather than a boolean so clicking the banner again after closing the
// modal still re-triggers it.
hcOpenSignal:    0,
addressInput:    "",
addressLoading:  false,
addressResult:   null,
addressError:    null,
// "Is this you?" owner-confirm flow — see lookupAddress / submitHomeClaim.
identityStep:     null,  // null | "confirm" | "edit" | "email" | "confirmed"
manualFirstName:  "",
manualLastName:   "",
// Holds the name/ownerMatch info once confirmed, while we wait on the
// "email" step below — submitHomeClaim() doesn't fire until that step
// resolves (either an email is entered or "No thanks" is clicked).
pendingClaim:     null,
// Optional — asked as its own step after the name so a claimed address
// can still go into a mailing list even for visitors who never create an
// account. Never required: "No thanks" fires the webhook with no email.
claimEmail:       "",
claimSubmitting:  false,
    };
  }

// Best-effort extraction of an owner name from the property-data response.
// Confirmed: the backend pulls from RentCast's Property Records API, which
// returns owner info as propData.owner = { names: ["John Smith", ...],
// type: "Individual"|"Organization", mailingAddress: {...} } — that shape is
// checked first. The rest are fallbacks in case propData isn't the raw
// RentCast object as-is, or the shape changes — safe to trim down once
// you've confirmed the real response in devtools.
extractOwnerName = (data) => {
if (!data) return null;
const s = data.subject || {};
const p = data.propData || {};
// Only ever accept an actual string here. Some property-data responses
// come back with these fields as an object (or null wrapped oddly)
// instead of a plain string — without this guard, String(thatObject)
// silently produces "[object Object]", which then gets split on
// whitespace into "[object" / "Object]" and shown to the visitor as a
// garbled owner name (the "object object" bug Tracy reported). Every
// candidate below is now explicitly typeof-checked before it's allowed
// into the list.
const asStr = (v) => (typeof v === "string" ? v : null);
const rentcastNames = p.owner && Array.isArray(p.owner.names) ? p.owner.names : null;
const candidates = [
asStr(rentcastNames && rentcastNames[0]),
asStr(s.ownerName), asStr(s.owner),
asStr(p.owner),
asStr(p.ownerName), asStr(p.owner1FullName),
[p.owner1FirstName, p.owner1LastName].filter(Boolean).join(" "),
[p.ownerFirstName, p.ownerLastName].filter(Boolean).join(" "),
asStr(data.ownerName), asStr(data.owner),
    ]
.filter(Boolean)
.map(v => String(v).trim())
.filter(v => v.length > 1);
if (!candidates.length) return null;
const full = candidates[0];
const parts = full.split(/\s+/);
return {
full,
first: parts[0] || "",
last:  parts.length > 1 ? parts.slice(1).join(" ") : "",
    };
  };

lookupAddress = async () => {
const address = (this.state.addressInput || "").trim();
if (!address) return;
this.setState({ addressLoading: true, addressError: null });
try {
const resp = await fetch("https://ai.myhomebuddha.com/.netlify/functions/property-data", {
method: "POST",
headers: { "Content-Type": "application/json" },
body: JSON.stringify({ address }),
      });
let result = { address };
let owner = null;
let lookupError = null;
if (resp.ok) {
const data = await resp.json();
const s = data.subject || {};
const p = data.propData || {};
result = {
address,
yearBuilt:  pickStr(s.yearBuilt, p.yearBuilt),
squareFeet: pickStr(s.sqft, p.squareFootage, p.livingArea),
bedrooms:   pickStr(s.beds, p.bedrooms),
bathrooms:  pickStr(s.baths, p.bathrooms),
county:     pickStr(p.county),
state:      pickStr(p.state, p.stateCode),
zipCode:    pickStr(p.zipCode, p.zip, p.postalCode),
        };
owner = this.extractOwnerName(data);
      } else {
// Non-throwing failure (e.g. a 500 from the Netlify function) — don't
// leave the visitor staring at a blank "confirm" step with no
// explanation of why nothing was found.
lookupError = "Couldn't auto-fill your home's details — you can still continue and enter them yourself.";
      }
const parts = address.split(",").map(x => x.trim());
result.city = parts.length >= 2 ? parts[1] : "";
result.ownerMatch = owner;
this.setState({
addressResult:   result,
addressLoading:  false,
addressError:    lookupError,
identityStep:    "confirm",
manualFirstName: owner ? owner.first : "",
manualLastName:  owner ? owner.last  : "",
      });
localStorage.setItem("mhb_pending_home", JSON.stringify(result));
    } catch (err) {
console.error("lookupAddress:", err);
this.setState({ addressLoading: false, addressError: "Couldn't look up that address — please try again." });
    }
  };

// Visitor clicked "Yes, that's me" on the public-record owner name —
// name is confirmed, but the webhook doesn't fire yet. Move to the
// "email" step and hold onto the name info until that resolves.
handleConfirmOwnerMatch = () => {
const owner = this.state.addressResult && this.state.addressResult.ownerMatch;
if (!owner) return;
this.setState({
identityStep: "email",
pendingClaim: { firstName: owner.first, lastName: owner.last, ownerMatch: true, matchedOwnerName: owner.full },
    });
  };

// Visitor clicked "That's not me" — drop into the manual name entry step.
handleRejectOwnerMatch = () => {
this.setState({ identityStep: "edit" });
  };

// Visitor typed their own name (either because we had no owner match at
// all, or because they rejected the one we found) and hit Confirm — same
// as handleConfirmOwnerMatch, moves to the "email" step rather than
// submitting immediately.
handleManualNameSubmit = () => {
const firstName = (this.state.manualFirstName || "").trim();
const lastName  = (this.state.manualLastName  || "").trim();
if (!firstName || !lastName) return;
const owner = this.state.addressResult && this.state.addressResult.ownerMatch;
this.setState({
identityStep: "email",
pendingClaim: { firstName, lastName, ownerMatch: false, matchedOwnerName: owner ? owner.full : null },
    });
  };

// Visitor entered an email on the "email" step and submitted it.
handleEmailSubmit = () => {
const email = (this.state.claimEmail || "").trim();
if (!this.state.pendingClaim) return;
this.submitHomeClaim({ ...this.state.pendingClaim, email: email || null });
  };

// Visitor clicked "No thanks" on the "email" step — webhook still fires,
// just with no email attached (same as before this step existed).
handleSkipEmail = () => {
if (!this.state.pendingClaim) return;
this.submitHomeClaim({ ...this.state.pendingClaim, email: null });
  };

// Fires the "home_claimed" GHL webhook, then advances to the "confirmed"
// step regardless of whether the webhook succeeded — a webhook hiccup
// should never block someone from moving on to create their account.
// Called only from handleEmailSubmit/handleSkipEmail, i.e. only once the
// "email" step has resolved one way or the other. email is optional and
// may be null — a claimed address can still land in GHL as a real,
// mailing-list-ready Contact even when the visitor skips it or never
// finishes creating an account.
submitHomeClaim = async ({ firstName, lastName, ownerMatch, matchedOwnerName, email }) => {
const r = this.state.addressResult || {};
this.setState({ claimSubmitting: true });
try {
await sendHomeClaimedWebhook({
firstName, lastName, ownerMatch, matchedOwnerName, email,
address: r.address, city: r.city, state: r.state, zipCode: r.zipCode,
yearBuilt: r.yearBuilt, squareFeet: r.squareFeet, bedrooms: r.bedrooms, bathrooms: r.bathrooms,
      });
    } finally {
const updatedPending = { ...r, firstName, lastName, email: email || r.email || null, ownerMatchConfirmed: ownerMatch };
localStorage.setItem("mhb_pending_home", JSON.stringify(updatedPending));
this.setState({ claimSubmitting: false, identityStep: "confirmed", addressResult: updatedPending, pendingClaim: null });
    }
  };
scrollToStep = (stepN) => {
if (stepN === 1) {
const el = document.getElementById("claimAddressBox");
if (el) el.scrollIntoView({ behavior: "smooth" });
return;
  }
const userLogin = localStorage.getItem("userLogin");
if (!userLogin) {
this.props.history.push({ pathname: "/login", state: { from: this.props.location.pathname } });
return;
  }
const el = document.getElementById("homePortalTabs");
if (el) el.scrollIntoView({ behavior: "smooth" });
};
handleSetupClick = () => {
const userLogin = localStorage.getItem("userLogin");
if (!userLogin) {
this.props.history.push({
pathname: "/login",
state: { from: this.props.location.pathname },
      });
return;
    }
this.setState({ showSetupMyHome: true });
  };

renderHook() {
const BADGES = [
      { icon: "🏠", label: "Organize Everything" },
      { icon: "🛡️", label: "Protect What Matters" },
      { icon: "❤️", label: "Improve With Confidence" },
    ];
const TOOLS = [
      { icon: "🔧", title: "Smart Maintenance Reminders", desc: "Never miss what matters. Get personalized reminders to keep your home running smoothly and avoid costly repairs." },
      { icon: "📸", title: "AI Photo Analysis", desc: "Take a photo, get answers. Our AI analyzes your furnace, water heater, electrical panel & more to flag issues." },
      { icon: "💬", title: "Ask My Home Anything", desc: "Get instant AI answers about your home, systems, repairs, upgrades, safety and more — 24/7." },
      { icon: "📈", title: "Track & Protect Your Investment", desc: "See your home's value over time and track the improvements and maintenance that build equity." },
      { icon: "🗂️", title: "Home Documents Vault", desc: "Store warranties, manuals, receipts, permits and inspections — all safe and easy to find." },
      { icon: "🛒", title: "Access Home Products", desc: "Find recommended products for your home, with exclusive deals on what you need to maintain and improve it." },
      { icon: "🤝", title: "Trusted Service Providers", desc: "Connect with rated local pros you can trust for any job — big or small." },
      { icon: "🩺", title: "Home Health Score", desc: "Get a score for your home's health, safety & efficiency, and see ideas to make it even better." },
    ];
const PORTAL_BENEFITS = [
      { icon: "🛟", label: "Stay Safe", desc: "Monitor systems, get alerts, and address issues before they become problems." },
      { icon: "💵", label: "Save Money", desc: "Prevent surprises and make smarter maintenance decisions." },
      { icon: "📊", label: "Build Value", desc: "Track improvements and maintenance that increase your home's value." },
      { icon: "🎯", label: "Make It Yours", desc: "Personalized tools, recommendations, and insights for YOUR home." },
      { icon: "🧘", label: "Peace of Mind", desc: "Everything you need to manage your home — anytime, anywhere." },
    ];
const BOTTOM_STRIP = [
      { icon: "🧠", label: "Know More, Worry Less", desc: "Get insights and confidence about your home." },
      { icon: "🔔", label: "Always One Step Ahead", desc: "Reminders and alerts keep you ahead of every season." },
      { icon: "🤝", label: "Connect With The Best", desc: "Find trusted pros and get the job done right." },
      { icon: "🏷️", label: "Exclusive Savings", desc: "Access special offers on quality products." },
    ];
const WHY_CLAIM = [
      { icon: "🔓", color: NAVY,  label: "Unlock your Home Portal instantly", sub: "No waiting, no cost — a lifetime of value" },
      { icon: "🎯", color: GOLD,  label: "Local offers made just for you", sub: "Discounted products & services matched to YOUR home's exact location" },
      { icon: "📊", color: GREEN, label: "See what your home is really worth", sub: "Right now — with your knowledge & our data (better than Zillow)" },
      { icon: "🧭", color: NAVY,  label: "Get a free, personalized action plan", sub: "So your home stays a happy, healthy, safe place for your family" },
      { icon: "💰", color: GOLD,  label: "See who's interested in buying", sub: "If you ever sell, potentially save thousands by selling direct" },
      { icon: "⏱️", color: GREEN, label: "Takes just 30 seconds", sub: "No credit card, no obligation, no hassles" },
    ];

return (
      <div style={{ marginBottom: "18px" }}>
        {/* Hero */}
        <div
style={{
background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 100%)`,
borderRadius: "16px",
padding: "30px 26px",
marginBottom: "16px",
boxShadow: "0 6px 24px rgba(26,34,128,0.18)",
textAlign: "center",
          }}
        >
          <div style={{ color: GOLD, fontWeight: 700, fontSize: "12px", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "6px" }}>
My Home Buddha · America's Home Advisor™
          </div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", marginBottom: "2px" }}>
Every Homeowner's Journey Starts Here
          </div>
          <h1 style={{ color: "white", fontWeight: 900, fontSize: "26px", margin: "0 0 4px", lineHeight: 1.25 }}>
Claim Your Home™
          </h1>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "14px", marginBottom: "16px", fontStyle: "italic" }}>
Build Your Home Operating System — One Step At A Time, or we can build it for you... For Free!
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap", marginBottom: "14px" }}>
            {BADGES.map(b => (
              <div key={b.label} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "50px", padding: "6px 14px", color: "white", fontSize: "12px", fontWeight: 700 }}>
                {b.icon} {b.label}
              </div>
            ))}
          </div>
          <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "12px", maxWidth: "480px", margin: "0 auto" }}>
Your personalized command center for a safer, healthier, more valuable home.
          </div>
        </div>

        {/* Why Claim Your Home Right Now */}
        <div style={{ marginBottom: "20px" }}>
          <div style={{ textAlign:"center", marginBottom:"14px" }}>
            <div
style={{
display:"inline-block",
background: `linear-gradient(135deg, ${GOLD}, #B8790F)`,
color:"#2A1600",
fontWeight:800,
fontSize:"11px",
textTransform:"uppercase",
letterSpacing:".06em",
borderRadius:"50px",
padding:"4px 14px",
marginBottom:"8px",
boxShadow:"0 3px 10px rgba(212,146,26,0.35)",
              }}
            >
🔥 6 Reasons To Act Now
            </div>
            <div style={{ fontSize:"17px", fontWeight:900, color: NAVY }}>Why Claim Your Home Right Now?</div>
          </div>
          <Row gutter={[14,14]}>
            {WHY_CLAIM.map((h,i) => (
              <Col key={i} xs={24} sm={12} md={8}>
                <div
style={{
background:"white",
border:`1px solid ${BORDER}`,
borderTop:`3px solid ${h.color}`,
borderRadius:"12px",
padding:"16px",
height:"100%",
display:"flex",
alignItems:"flex-start",
gap:"12px",
boxShadow:"0 2px 8px rgba(26,34,128,0.07)",
transition:"transform .15s ease, box-shadow .15s ease",
                  }}
onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 10px 22px rgba(26,34,128,0.16)"; }}
onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(26,34,128,0.07)"; }}
                >
                  <div
style={{
width:"38px",
height:"38px",
minWidth:"38px",
borderRadius:"50%",
background: h.color,
display:"flex",
alignItems:"center",
justifyContent:"center",
fontSize:"18px",
boxShadow:`0 4px 10px ${h.color}55`,
                    }}
                  >
                    {h.icon}
                  </div>
                  <div>
                    <div style={{ fontSize:"13px", color:"#1A202C", fontWeight:800, lineHeight:1.3, marginBottom:"3px" }}>{h.label}</div>
                    <div style={{ fontSize:"11px", color: MUTED, lineHeight:1.5 }}>{h.sub}</div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Second "Claim Your Home" CTA — a repeat of the top banner's ask, placed
            here so visitors who don't scroll all the way back up (or who bail
            out partway down the page) still get a clear chance to start. Scrolls
            back up to the same address box the top banner uses, rather than
            duplicating the whole form. */}
        <div
style={{
background: `linear-gradient(135deg, ${NAVY}, #12185C)`,
border: `2px solid ${GOLD}`,
borderRadius: "12px",
padding: "20px 22px",
marginBottom: "16px",
textAlign: "center",
boxShadow: "0 6px 20px rgba(26,34,128,0.16)",
          }}
        >
          <div style={{ fontSize: "22px", marginBottom: "4px" }}>📍</div>
          <div style={{ color: "white", fontWeight: 900, fontSize: "18px", marginBottom: "4px" }}>
Haven't Claimed Your Home Yet?
          </div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", marginBottom: "14px", maxWidth: "440px", margin: "0 auto 14px", lineHeight: 1.6 }}>
It only takes your address — free, no commitment, and it's the first step of your Home Operating System below.
          </div>
          <Button
type="primary"
onClick={() => this.scrollToStep(1)}
style={{ borderRadius: "50px", background: GOLD, border: "none", fontWeight: 700, color: "#2A1600", padding: "0 26px", height: "38px" }}
          >
Claim Your Home™ Now
          </Button>
        </div>

        <div style={{ background: NAVY, borderRadius:"12px", padding:"18px 16px", marginBottom:"16px" }}>
          <div style={{ textAlign:"center", color: GOLD, fontWeight:800, fontSize:"13px", marginBottom:"12px", textTransform:"uppercase", letterSpacing:".05em" }}>
Your 8-Step Home Operating System
          </div>
          <Row gutter={[8,8]}>
            {[
              { n:1, icon:"📍", label:"Claim Your Home" },
              { n:2, icon:"👤", label:"Create Free Account" },
              { n:3, icon:"🩺", label:"Health Check-Up" },
              { n:4, icon:"🔧", label:"Maintenance Plan" },
              { n:5, icon:"📈", label:"Track Your Value" },
              { n:6, icon:"🎯", label:"Set Your Goals" },
              { n:7, icon:"🛒", label:"Unlock Savings" },
              { n:8, icon:"🤖", label:"Ask AI Anything" },
            ].map(s => (
              <Col key={s.n} xs={12} sm={6} md={3}>
                <div onClick={() => this.scrollToStep(s.n)}
style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:"10px", padding:"10px 8px", textAlign:"center", height:"100%", cursor:"pointer", transition:"background .15s" }}
onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}>
                  <div style={{ fontSize:"18px" }}>{s.icon}</div>
                  <div style={{ fontSize:"10px", color:"white", fontWeight:700, marginTop:"4px" }}>{s.n}. {s.label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
        {/* Tools grid */}
        <div style={{ marginBottom: "8px", textAlign: "center" }}>
          <div style={{ fontSize: "15px", fontWeight: 800, color: NAVY }}>Powerful Tools Built For Homeowners</div>
        </div>
        <Row gutter={[12, 12]} style={{ marginBottom: "16px" }}>
          {TOOLS.map(t => (
            <Col key={t.title} xs={24} sm={12} md={6}>
              <div style={{ background: "white", border: `1px solid ${BORDER}`, borderRadius: "12px", padding: "16px", height: "100%" }}>
                <div style={{ fontSize: "24px", marginBottom: "6px" }}>{t.icon}</div>
                <div style={{ fontWeight: 700, color: NAVY, fontSize: "12px", marginBottom: "4px" }}>{t.title}</div>
                <div style={{ fontSize: "11px", color: MUTED, lineHeight: 1.6 }}>{t.desc}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Sample dashboard preview */}
        <div style={{ background: "white", border: `1px solid ${BORDER}`, borderRadius: "12px", padding: "18px 20px", marginBottom: "16px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "12px", textAlign: "center" }}>
Your Home Portal Dashboard — Sample Preview
          </div>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={8} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "11px", color: MUTED, marginBottom: "2px" }}>Home Health Score</div>
              <div style={{ fontSize: "28px", fontWeight: 900, color: GREEN }}>85</div>
              <div style={{ fontSize: "11px", color: GREEN, fontWeight: 700 }}>Great!</div>
              <div style={{ fontSize: "10px", color: MUTED, marginTop: "4px" }}>Safety · Systems · Maintenance · Efficiency · Environment</div>
            </Col>
            <Col xs={24} sm={8} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "11px", color: MUTED, marginBottom: "2px" }}>Home Value Tracker</div>
              <div style={{ fontSize: "22px", fontWeight: 900, color: NAVY }}>$725,000</div>
              <div style={{ fontSize: "11px", color: GREEN, fontWeight: 700 }}>+$48,500 (7.2%) vs 6 mo ago</div>
            </Col>
            <Col xs={24} sm={8} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "11px", color: MUTED, marginBottom: "2px" }}>Upcoming Maintenance</div>
              <div style={{ fontSize: "12px", color: "#1A202C", lineHeight: 1.9 }}>
Change AC Filter — 10 days<br />
Inspect Sump Pump — 21 days<br />
Service Water Heater — 45 days
              </div>
            </Col>
          </Row>
        </div>

        {/* Portal benefits */}
        <div style={{ background: `linear-gradient(135deg, ${GOLD}, #B8790F)`, borderRadius: "12px", padding: "18px 22px", marginBottom: "16px" }}>
          <div style={{ color: "#2A1600", fontWeight: 800, fontSize: "15px", marginBottom: "12px", textAlign: "center" }}>
All The Tools You Need. One Powerful Portal.
          </div>
          <Row gutter={[8, 8]}>
            {PORTAL_BENEFITS.map(b => (
              <Col key={b.label} xs={12} sm={8} md={4} style={{ flex: "1 1 auto" }}>
                <div style={{ background: "rgba(255,255,255,0.5)", borderRadius: "8px", padding: "10px 8px", textAlign: "center", height: "100%" }}>
                  <div style={{ fontSize: "18px" }}>{b.icon}</div>
                  <div style={{ fontSize: "11px", color: "#2A1600", fontWeight: 800, marginTop: "4px" }}>{b.label}</div>
                  <div style={{ fontSize: "10px", color: "#2A1600", opacity: 0.8, marginTop: "2px", lineHeight: 1.4 }}>{b.desc}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Testimonial */}
        <div style={{ textAlign: "center", padding: "14px 20px", marginBottom: "16px" }}>
          <div style={{ fontSize: "13px", color: "#1A202C", fontStyle: "italic", marginBottom: "6px" }}>
"My Home Portal organized everything in my home and has given me peace of mind knowing my home is safe for my family"
          </div>
          <div style={{ fontSize: "11px", color: MUTED }}>— Real Homeowner &nbsp;★★★★★</div>
        </div>

        {/* Bottom benefit strip */}
        <Row gutter={[10, 10]} style={{ marginBottom: "16px" }}>
          {BOTTOM_STRIP.map(b => (
            <Col key={b.label} xs={12} md={6}>
              <div style={{ textAlign: "center", padding: "10px 6px" }}>
                <div style={{ fontSize: "20px" }}>{b.icon}</div>
                <div style={{ fontSize: "11px", fontWeight: 700, color: NAVY, marginTop: "4px" }}>{b.label}</div>
                <div style={{ fontSize: "10px", color: MUTED, marginTop: "2px" }}>{b.desc}</div>
              </div>
            </Col>
          ))}
        </Row>

       <div id="claimAddressBox" style={{ background: "white", border: `2px solid ${GOLD}`, borderRadius: "12px", padding: "22px 24px", marginBottom: "16px", textAlign: "center" }}>
          <div style={{ fontSize: "24px", marginBottom: "6px" }}>📍</div>
          <div style={{ fontWeight: 800, color: NAVY, fontSize: "16px", marginBottom: "6px" }}>Enter Your Address To Claim Your Home</div>
          <div style={{ fontSize: "12px", color: MUTED, marginBottom: "14px", maxWidth: "480px", margin: "0 auto 14px", lineHeight: 1.6 }}>
Confirm your home details and make sure you get access to the best local resources for products, service providers and more — and start building your digital twin of your home now.
          </div>
          <div style={{ display: "flex", gap: "8px", maxWidth: "440px", margin: "0 auto", flexWrap: "wrap", justifyContent: "center" }}>
            <Input
placeholder="123 Main St, City, State ZIP"
value={this.state.addressInput}
onChange={e => this.setState({ addressInput: e.target.value })}
onPressEnter={this.lookupAddress}
style={{ flex: 1, minWidth: "220px", borderRadius: "8px" }}
            />
            <Button
type="primary"
loading={this.state.addressLoading}
onClick={this.lookupAddress}
style={{ borderRadius: "8px", background: GOLD, border: "none", fontWeight: 700, color: "#2A1600" }}
            >
Confirm My Home Address
            </Button>
          </div>
          {this.state.addressError && (
            <div style={{ color: "#C53030", fontSize: "12px", marginTop: "10px" }}>{this.state.addressError}</div>
          )}
          {this.state.addressResult && (
            <div style={{ background: "#F0FFF4", border: "1px solid #9AE6B4", borderRadius: "10px", padding: "14px 16px", marginTop: "14px", maxWidth: "440px", marginLeft: "auto", marginRight: "auto", textAlign: "left" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: "#276749", marginBottom: "6px" }}>✓ Found it! Here's a preview:</div>
              <div style={{ fontSize: "12px", color: "#2D3748", lineHeight: 1.8 }}>
                {this.state.addressResult.yearBuilt && <span>Built {this.state.addressResult.yearBuilt} · </span>}
                {this.state.addressResult.squareFeet && <span>{this.state.addressResult.squareFeet} sq ft · </span>}
                {this.state.addressResult.bedrooms && this.state.addressResult.bathrooms && <span>{this.state.addressResult.bedrooms} bed / {this.state.addressResult.bathrooms} bath</span>}
              </div>

              {/* Step 1a: show whatever public-record name we found (or say plainly
                  that we didn't find one) and let the visitor confirm/reject it */}
              {this.state.identityStep === "confirm" && (
                <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #C6F6D5" }}>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: NAVY, marginBottom: "8px", lineHeight: 1.5 }}>
Public records show this home's owner as{" "}
<strong>{this.state.addressResult.ownerMatch ? this.state.addressResult.ownerMatch.full : "Not Found Not Found"}</strong>
                    {this.state.addressResult.ownerMatch ? " — is that you?" : ""}
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {this.state.addressResult.ownerMatch && (
                      <Button
type="primary"
loading={this.state.claimSubmitting}
onClick={this.handleConfirmOwnerMatch}
style={{ borderRadius: "50px", background: NAVY, border: "none", fontWeight: 700, flex: 1 }}
                      >
✓ Yes, that's me
                      </Button>
                    )}
                    <Button
onClick={this.handleRejectOwnerMatch}
disabled={this.state.claimSubmitting}
style={{ borderRadius: "50px", fontWeight: 700, flex: 1 }}
                    >
{this.state.addressResult.ownerMatch ? "That's not me" : "That's not me, name not found"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 1b: visitor said "that's not me" (or there was no name to
                  confirm in the first place) — self-report */}
              {this.state.identityStep === "edit" && (
                <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #C6F6D5" }}>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: NAVY, marginBottom: "8px" }}>
                    {this.state.addressResult.ownerMatch ? "No problem — what's your name?" : "We couldn't find an owner match on file — what's your name?"}
                  </div>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                    <Input
placeholder="First name"
value={this.state.manualFirstName}
onChange={e => this.setState({ manualFirstName: e.target.value })}
style={{ flex: 1, minWidth: "120px", borderRadius: "8px" }}
                    />
                    <Input
placeholder="Last name"
value={this.state.manualLastName}
onChange={e => this.setState({ manualLastName: e.target.value })}
style={{ flex: 1, minWidth: "120px", borderRadius: "8px" }}
                    />
                  </div>
                  <Button
type="primary"
block
loading={this.state.claimSubmitting}
disabled={!this.state.manualFirstName.trim() || !this.state.manualLastName.trim()}
onClick={this.handleManualNameSubmit}
style={{ borderRadius: "50px", background: NAVY, border: "none", fontWeight: 700 }}
                  >
Confirm &amp; Continue
                  </Button>
                </div>
              )}

              {/* Step 2: name confirmed — ask for email before the webhook fires */}
              {this.state.identityStep === "email" && (
                <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #C6F6D5" }}>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: NAVY, marginBottom: "8px", lineHeight: 1.5 }}>
Enter your email if you would like access to local monthly discounts on home products and services.
                  </div>
                  <Input
type="email"
placeholder="you@example.com"
value={this.state.claimEmail}
onChange={e => this.setState({ claimEmail: e.target.value })}
onPressEnter={this.handleEmailSubmit}
style={{ borderRadius: "8px", marginBottom: "8px" }}
                  />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button
type="primary"
loading={this.state.claimSubmitting}
disabled={!this.state.claimEmail.trim()}
onClick={this.handleEmailSubmit}
style={{ borderRadius: "50px", background: NAVY, border: "none", fontWeight: 700, flex: 1 }}
                    >
✓ Send Me Local Discounts
                    </Button>
                    <Button
onClick={this.handleSkipEmail}
disabled={this.state.claimSubmitting}
style={{ borderRadius: "50px", fontWeight: 700, flex: 1 }}
                    >
No thanks
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: identity captured & webhook fired — hand off to account creation */}
              {this.state.identityStep === "confirmed" && (
                <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #C6F6D5" }}>
                  <div style={{ fontSize: "12px", color: "#276749", fontWeight: 700, marginBottom: "10px" }}>
🎉 Thanks, {this.state.addressResult.firstName}! Now it's time to create your account.
                  </div>
                  <Button
type="primary"
block
onClick={() => this.props.history.push({ pathname: "/register", state: { from: this.props.location.pathname } })}
style={{ borderRadius: "50px", background: NAVY, border: "none", fontWeight: 700 }}
                  >
Create My Free Account &amp; Save These Details →
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Home Health Check-Up — moved up here (rather than leaving it
            buried in the tabs below) since a lot of visitors were bailing
            before scrolling that far. No login required to take it — see
            HomeBuddha.js's hcOpen()/hcFinish(); results are computed and
            shown either way, and the results screen offers "Create an
            account to save your results" for anyone not logged in. Clicking
            below bumps hcOpenSignal, which pops the quiz modal on the
            <HomeBuddha /> instance mounted further down the page. */}
        <div style={{ background: "linear-gradient(135deg,#C53030,#9B2C2C)", borderRadius: "12px", padding: "22px 24px", marginBottom: "16px", textAlign: "center", boxShadow: "0 2px 12px rgba(197,48,48,0.3)" }}>
          <div style={{ fontSize: "34px", marginBottom: "6px" }}>😟</div>
          <div style={{ color: "#FFD98E", fontWeight: 800, fontSize: "16px", marginBottom: "6px" }}>How Safe And Healthy Is Your Home?</div>
          <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "13px", marginBottom: "14px", maxWidth: "480px", margin: "0 auto 14px", lineHeight: 1.6 }}>
Take the <strong>Happy Home Buddha Health Check-Up</strong> — 29 questions, 5 minutes, no account needed. You'll know exactly where your home stands, and you can save your results later.
          </div>
          <Button
type="primary"
onClick={() => this.setState(prev => ({ hcOpenSignal: prev.hcOpenSignal + 1 }))}
style={{ borderRadius: "50px", background: GOLD, border: "none", fontWeight: 700, color: "#2A1600", padding: "0 26px", height: "38px" }}
          >
🚀 Start My Health Check-Up
          </Button>
        </div>

        <div style={{ textAlign: "center", marginBottom: "4px" }}>
          <div style={{ display: "inline-block", background: NAVY, color: "white", borderRadius: "50px", padding: "10px 24px", fontWeight: 800, fontSize: "13px" }}>
👇 Explore Your Home Portal Below
          </div>
          <div style={{ fontSize: "11px", color: MUTED, marginTop: "10px", fontStyle: "italic" }}>
Better Home. Better Life. Built Together.
          </div>
        </div>
      </div>
    );
  }

render() {
const userLogin = localStorage.getItem("userLogin");
const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
const isNonAgent =
userDetails &&
userDetails.userProfile !== "AG" &&
userDetails.userProfile !== "L";
let pendingCapture = null;
try { pendingCapture = JSON.parse(localStorage.getItem("mhb_pending_home") || "null"); } catch(e) {}
const { sectionKey, subId } = (this.props.match && this.props.match.params) || {};
// FB (or any) ad can encode its own target area right in the destination
// URL — e.g. .../homePortal/home_health/hvac?zip=37203&city=Nashville&state=TN —
// so the right local product/service ad shows up without guessing at the
// visitor's location. This takes priority over a logged-in home's saved
// address inside HomeBuddha.js's geo lookup.
const adParams = new URLSearchParams(this.props.location.search);
const adGeo = {
cityName: adParams.get("city") || "",
stateName: adParams.get("state") || "",
postalCode: adParams.get("zip") || "",
    };
const hasAdGeo = adGeo.cityName || adGeo.stateName || adGeo.postalCode;
const capturedGeo = this.state.addressResult ? {
cityName: this.state.addressResult.city || "",
stateName: this.state.addressResult.state || "",
postalCode: this.state.addressResult.zipCode || "",
    } : null;
const finalAdGeo = hasAdGeo ? adGeo : (capturedGeo || adGeo);

return (
      <div style={{ backgroundColor: "#F9FAFF", minHeight: "70vh" }}>
        <Row className="margin-body-agent">
          <Col span={24} style={{ marginTop: "1vw", marginBottom: "0.5vw" }}>
            <h2 style={{ color: NAVY, fontWeight: 800 }}>🏡 Home Portal</h2>
          </Col>
        </Row>
        {!userLogin && (
          <Row className="margin-body-agent">
            <Col span={24}>{this.renderHook()}</Col>
          </Row>
        )}
        <Row className="margin-body-agent">
          <Col
id="homePortalTabs"
span={24}
style={{
background: "white",
borderRadius: 10,
marginBottom: "2vw",
overflow: "hidden",
            }}
          >
            {isNonAgent && (
              <div style={{ padding: "12px 16px 0" }}>
                <Button
type="primary"
icon={<PlusOutlined />}
onClick={this.handleSetupClick}
style={{
borderRadius: "50px",
background: NAVY,
border: "none",
fontWeight: 700,
marginBottom: "8px",
                  }}
                >
                  {pendingCapture && pendingCapture.address ? "🏡 Set Up My Home / Continue Where I Left Off" : "🏡 Set Up My Home"}
                </Button>
              </div>
            )}
            <HomeBuddha
showSetupModal={this.state.showSetupMyHome}
onCloseSetupModal={() => this.setState({ showSetupMyHome: false })}
onShowSetupModal={() => this.setState({ showSetupMyHome: true })}
openHealthCheckSignal={this.state.hcOpenSignal}
initialSection={sectionKey}
initialSubId={subId}
enableUrlSync={true}
adGeo={adGeo}
            />
          </Col>
        </Row>
      </div>
    );
  }
}

export default withAppContext(withRouter(HomePortalPage));